"use client";

import { forwardRef, useEffect, useRef, useState } from "react";

import { format, isValid, parse, type Locale } from "date-fns";
import { bs } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";

import { cn } from "utils/cn";

import Button from "components/Button";
import CalendarComponent from "components/Calendar";
import { FormControl, FormItem, FormLabel } from "components/Form";
import Input from "components/Input";
import { Popover, PopoverContent, PopoverTrigger } from "components/Popover";

interface DatePickerInputProps {
  value?: Date | string;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  calendarProps?: {
    disabled?: (date: Date) => boolean;
    locale?: Locale;
  };
  showCalendarIcon?: boolean;
}

export const DatePickerInput = forwardRef<HTMLInputElement, DatePickerInputProps>(
  ({ value, onChange, placeholder = "dd.mm.yyyy", disabled = false, calendarProps }, ref) => {
    const [inputValue, setInputValue] = useState("");
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [isUserEditing, setIsUserEditing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (isUserEditing) return;

      if (value) {
        if (typeof value === "string") {
          if (value.includes(".")) {
            const parsedDate = parse(value, "dd.MM.yyyy", new Date());
            if (isValid(parsedDate)) {
              setInputValue(value);
              setSelectedDate(parsedDate);
            }
          }
        } else if (value instanceof Date && isValid(value)) {
          const formattedValue = format(value, "dd.MM.yyyy");
          setInputValue(formattedValue);
          setSelectedDate(value);
        }
      } else {
        setInputValue("");
        setSelectedDate(undefined);
      }
    }, [value, isUserEditing]);

    const getDigitsOnly = (str: string) => str.replace(/\D/g, "");

    const formatDateInput = (digitsOnly: string) => {
      if (digitsOnly.length === 0) return "";
      if (digitsOnly.length <= 2) return digitsOnly;
      if (digitsOnly.length <= 4) {
        const day = digitsOnly.slice(0, 2);
        const month = digitsOnly.slice(2, 4);
        return `${day}.${month}`;
      }
      const day = digitsOnly.slice(0, 2);
      const month = digitsOnly.slice(2, 4);
      const year = digitsOnly.slice(4, 8);
      return `${day}.${month}.${year}`;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsUserEditing(true);
      const inputVal = e.target.value;
      const digitsOnly = getDigitsOnly(inputVal);

      let formatted = "";
      let shouldMoveCursor = false;
      let targetPosition = 0;

      if (digitsOnly.length === 1) {
        const firstDigit = Number.parseInt(digitsOnly[0]);
        if (firstDigit > 3) {
          formatted = `0${digitsOnly}.`;
          shouldMoveCursor = true;
          targetPosition = 3;
        } else {
          formatted = digitsOnly;
        }
      } else if (digitsOnly.length === 2) {
        const day = Number.parseInt(digitsOnly);
        const correctedDay = day > 31 ? "31" : digitsOnly;
        formatted = `${correctedDay}.`;
        shouldMoveCursor = true;
        targetPosition = 3;
      } else if (digitsOnly.length === 3) {
        const day = Number.parseInt(digitsOnly.slice(0, 2));
        const correctedDay = day > 31 ? "31" : digitsOnly.slice(0, 2);

        const monthFirstDigit = Number.parseInt(digitsOnly[2]);
        if (monthFirstDigit >= 2) {
          formatted = `${correctedDay}.0${digitsOnly[2]}.`;
          shouldMoveCursor = true;
          targetPosition = 6;
        } else {
          formatted = `${correctedDay}.${digitsOnly[2]}`;
        }
      } else if (digitsOnly.length === 4) {
        const day = Number.parseInt(digitsOnly.slice(0, 2));
        const month = Number.parseInt(digitsOnly.slice(2, 4));

        const correctedDay = day > 31 ? "31" : digitsOnly.slice(0, 2);
        const correctedMonth = month > 12 ? "12" : digitsOnly.slice(2, 4);

        formatted = `${correctedDay}.${correctedMonth}.`;
        shouldMoveCursor = true;
        targetPosition = 6;
      } else if (digitsOnly.length > 4) {
        const day = Number.parseInt(digitsOnly.slice(0, 2));
        const month = Number.parseInt(digitsOnly.slice(2, 4));

        const correctedDay = day > 31 ? "31" : digitsOnly.slice(0, 2);
        const correctedMonth = month > 12 ? "12" : digitsOnly.slice(2, 4);
        const year = digitsOnly.slice(4, 8);

        formatted = `${correctedDay}.${correctedMonth}.${year}`;
      }

      setInputValue(formatted);

      if (shouldMoveCursor) {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(targetPosition, targetPosition);
          }
        }, 0);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        e.preventDefault();
        setIsUserEditing(true);
        const input = inputRef.current;
        if (!input) return;

        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;
        const currentVal = inputValue;

        if (selectionStart !== selectionEnd) {
          const newVal = currentVal.slice(0, selectionStart) + currentVal.slice(selectionEnd);
          setInputValue(newVal);
          setTimeout(() => {
            if (input) input.setSelectionRange(selectionStart, selectionStart);
          }, 0);
          return;
        }

        let newVal = currentVal;
        if (selectionStart > 0) {
          const charBefore = currentVal[selectionStart - 1];
          if (charBefore === ".") {
            newVal = currentVal.slice(0, selectionStart - 2) + currentVal.slice(selectionStart);
            setInputValue(newVal);
            setTimeout(() => {
              if (input) input.setSelectionRange(selectionStart - 2, selectionStart - 2);
            }, 0);
          } else {
            newVal = currentVal.slice(0, selectionStart - 1) + currentVal.slice(selectionStart);
            setInputValue(newVal);
            setTimeout(() => {
              if (input) input.setSelectionRange(selectionStart - 1, selectionStart - 1);
            }, 0);
          }
        }
      }
    };

    const handleInputBlur = () => {
      const digitsOnly = getDigitsOnly(inputValue);

      if (digitsOnly.length === 8) {
        const day = digitsOnly.slice(0, 2);
        const month = digitsOnly.slice(2, 4);
        const year = digitsOnly.slice(4, 8);

        const dateStr = `${day}.${month}.${year}`;
        const parsedDate = parse(dateStr, "dd.MM.yyyy", new Date());

        if (isValid(parsedDate)) {
          setSelectedDate(parsedDate);
          setInputValue(dateStr);
          onChange?.(parsedDate);
        } else {
          setInputValue("");
          setSelectedDate(undefined);
          onChange?.(undefined);
        }
      } else if (digitsOnly.length === 0) {
        setInputValue("");
        setSelectedDate(undefined);
        onChange?.(undefined);
      } else {
        const formatted = formatDateInput(digitsOnly);
        setInputValue(formatted);
      }
      setIsUserEditing(false);
    };

    const handleCalendarSelect = (date: Date | undefined) => {
      if (date) {
        const formattedDate = format(date, "dd.MM.yyyy");
        setInputValue(formattedDate);
        setSelectedDate(date);
        onChange?.(date);
        setOpen(false);
      } else {
        setInputValue("");
        setSelectedDate(undefined);
        onChange?.(undefined);
        setOpen(false);
      }
      setIsUserEditing(false);
    };

    return (
      <div className="flex gap-2 items-center relative">
        <Input
          ref={(node) => {
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            if (inputRef) {
              inputRef.current = node;
            }
          }}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              disabled={disabled}
              className={cn(
                "absolute right-0 top-0 h-12 px-3 hover:bg-transparent",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={handleCalendarSelect}
              disabled={calendarProps?.disabled}
              locale={calendarProps?.locale || bs}
              captionLayout="dropdown"
              month={selectedDate}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

export default function FormDatePicker({ label, ...datePickerProps }: any) {
  return (
    <FormItem>
      {label && <FormLabel>{label}</FormLabel>}
      <FormControl>
        <DatePickerInput {...datePickerProps} />
      </FormControl>
    </FormItem>
  );
}
